import type { SkeletonProps } from '@mui/material/Skeleton'
import Skeleton from '@mui/material/Skeleton'
import type { SxProps, Theme } from '@mui/material/styles'
import React from 'react'

import { borderRadius, colors } from './KestrixDesignSystem.jsx'

export type KestrixSkeletonTone = 'subtle' | 'strong'

export type KestrixSkeletonProps = Omit<SkeletonProps, 'variant'> & {
  /**
   * Visual variant — same as MUI's `Skeleton` `variant`. Defaults to
   * `'rounded'` because design-system placeholders are pill-shaped, not
   * underline-style text bars.
   */
  variant?: SkeletonProps['variant']
  /**
   * Background tone:
   *  - `'subtle'` (default) → `colors.neutral[100]` for chrome placeholders
   *    (titles, counts, tab labels) sitting on the secondary surface.
   *  - `'strong'` → `colors.neutral[200]` for body-content placeholders
   *    (table cells, card values) where the surface is white.
   */
  tone?: KestrixSkeletonTone
}

const TONE_BG: Record<KestrixSkeletonTone, string> = {
  subtle: colors.neutral[100],
  strong: colors.neutral[200]
}

/**
 * Thin wrapper around MUI's `Skeleton` that bakes in the Kestrix neutral
 * palette and a rounded corner token by default, so consumers don't have to
 * re-state `bgcolor` / `borderRadius` on every placeholder bar.
 *
 * For a row-shaped placeholder inside a `<TableBody>`, use
 * {@link KestrixTableSkeleton} instead — this component is for individual
 * inline shapes (counts, labels, chips, tab placeholders).
 */
export const KestrixSkeleton = ({
  variant = 'rounded',
  tone = 'subtle',
  sx,
  ...rest
}: KestrixSkeletonProps): React.ReactElement => {
  const baseSx: SxProps<Theme> = {
    bgcolor: TONE_BG[tone],
    borderRadius: borderRadius.sm
  }

  return (
    <Skeleton
      variant={variant}
      sx={[baseSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    />
  )
}

export default KestrixSkeleton
